import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { useUploadQueue } from "@/lib/useUploadQueue";
import { resolveUrl } from "@/lib/api";
import { MaterialIcon } from "@/components/MaterialIcon";
import { Badge, PageTitle, btnPrimary, card, statusTone } from "@/components/admin/ui";
import { AddVideoDialog } from "@/pages/admin/AddVideoDialog";
import { UploadPanel } from "@/pages/admin/UploadPanel";

const Row = ({ v }) => (
  <Link to={`/admin/videos/${v.id}`} className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 p-3 hover:bg-[#F9FAFB] transition-colors" data-testid={`admin-video-row-${v.id}`}>
    <div className="w-24 sm:w-28 aspect-video rounded bg-[#E5E7EB] overflow-hidden shrink-0">
      {v.thumbnail_url && <img src={resolveUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />}
    </div>
    <div className="flex-1 min-w-[150px] sm:min-w-0">
      <p className="text-[14px] font-semibold truncate" data-testid="admin-video-title">{v.title}</p>
      <p className="text-[12px] text-[#6B7280] truncate">
        {v.source_type === "youtube" ? "YouTube" : "Uploaded"}
        {v.festival_name ? ` · ${v.festival_name}` : ""}
        {v.dance_styles?.length ? ` · ${v.dance_styles.join(", ")}` : ""}
        {v.tags?.length ? ` · ${v.tags.join(", ")}` : ""}
      </p>
    </div>
    <div className="flex flex-1 min-w-[180px] items-center gap-2 shrink-0 sm:flex-none">
      <Badge tone={statusTone(v.status)}>{v.status === "processing" && v.encode_progress ? `processing ${v.encode_progress}%` : v.status}</Badge>
      <Badge tone={v.visibility === "public" ? "gray" : v.visibility === "unlisted" ? "blue" : "red"}>{v.visibility}</Badge>
    </div>
  </Link>
);

export default function AdminVideos() {
  const { data: videos, reload } = useApi("/admin/videos");
  const [open, setOpen] = useState(false);
  const queue = useUploadQueue(reload);

  const pending = videos?.some((v) => ["processing", "uploading"].includes(v.status));
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, [pending, reload]);

  return (
    <div data-testid="admin-videos-page">
      <PageTitle title="Videos">
        <button type="button" className={btnPrimary} onClick={() => setOpen(true)} data-testid="add-video-button">
          <MaterialIcon name="add" size={20} weight={600} />
          Add Video
        </button>
      </PageTitle>
      <UploadPanel jobs={queue.jobs} onClear={queue.clearFinished} />
      <div className={`${card} divide-y divide-[#E5E7EB]`} data-testid="admin-video-list">
        {videos?.map((v) => <Row key={v.id} v={v} />)}
        {videos && videos.length === 0 && <p className="p-8 text-center text-[14px] text-[#6B7280]">No videos yet. Click “Add Video” to get started.</p>}
        {!videos && <p className="p-8 text-center text-[14px] text-[#6B7280]">Loading…</p>}
      </div>
      <AddVideoDialog open={open} onOpenChange={setOpen} onAdded={reload} addFiles={queue.addFiles} jobs={queue.jobs} />
    </div>
  );
}
